import * as Sentry from '@sentry/react-native';
import { isTransientNetworkError, toError } from './errorUtils.js';
import logger from './logger.js';
import supabase from './supabaseClient.js';

/**
 * Database service with functions to interact with Supabase
 */
export const DatabaseService = {
  /**
   * Save onboarding answers to the database
   * @param {string} userId - The user's ID
   * @param {object} answers - The onboarding answers
   * @returns {Promise} - Result of the database operation
   */
  saveOnboardingAnswers: async (userId, answers, intent) => {
    const maxRetries = 3;
    const baseDelay = 1000; // 1 second

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        logger.log(
          `[DatabaseService] Saving onboarding answers for user ${userId} (attempt ${attempt}/${maxRetries})`
        );

        // Call the secure RPC function with the new, descriptive name.
        const { data, error } = await supabase.rpc('save_onboarding_answers_early_signup', {
          p_user_id: userId,
          p_answers: answers,
          p_intent: intent || null,
        });

        if (error) throw error;

        logger.log(
          `[DatabaseService] Successfully saved onboarding answers for user ${userId} via RPC.`
        );
        return { data, error: null };
      } catch (error) {
        const isLastAttempt = attempt === maxRetries;
        const isNetworkError =
          error.message?.includes('Load failed') ||
          error.message?.includes('NetworkError') ||
          error.message?.includes('Failed to fetch');

        logger.error(
          `[DatabaseService] Error saving onboarding answers (attempt ${attempt}/${maxRetries}):`,
          {
            userId,
            errorMessage: error.message,
            errorCode: error.code,
            isNetworkError,
            willRetry: !isLastAttempt && isNetworkError,
          }
        );

        // Retry on network errors (common on mobile devices)
        if (isNetworkError && !isLastAttempt) {
          const delay = baseDelay * Math.pow(2, attempt - 1); // Exponential backoff
          logger.log(`[DatabaseService] Retrying in ${delay}ms...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        // On final attempt or non-network error, capture to Sentry
        Sentry.captureException(error, {
          tags: {
            operation: 'saveOnboardingAnswers',
            service: 'database',
            attempt: attempt.toString(),
            isNetworkError: isNetworkError.toString(),
          },
          contexts: { user: { id: userId } },
        });
        return { data: null, error };
      }
    }

    // This should never be reached, but needed for completeness
    return { data: null, error: new Error('Unexpected: retry loop completed without return') };
  },

  /**
   * Create a user profile in the public users table
   * @param {string} userId - The user's ID
   * @param {string} email - The user's email
   * @param {object} profileData - Additional profile data
   * @returns {Promise} - Result of the database operation
   */
  createUserProfile: async (userId, email, profileData = {}) => {
    try {
      const { data, error } = await supabase.from('users').upsert(
        {
          id: userId,
          email: email, // Include email in case it needs to be updated or for completeness
          tier: 'Free',
          gdpr_consent_given: true,
          profile_data: {
            ...profileData,
            created_via: 'onboarding',
          },
          updated_at: new Date().toISOString(), // Explicitly update the timestamp
        },
        {
          onConflict: 'id', // Specify the column that causes a conflict (the primary key)
        }
      );

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error creating user profile:', error.message);
      Sentry.captureException(error, {
        tags: { operation: 'createUserProfile', service: 'database' },
        contexts: { user: { id: userId } },
      });
      return { data: null, error };
    }
  },

  /**
   * Get user profile by ID
   * @param {string} userId - The user's ID
   * @returns {Promise} - The user profile data
   */
  getUserProfile: async (userId) => {
    try {
      const { data, error } = await supabase.from('users').select('*').eq('id', userId).single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error fetching user profile:', error?.message);
      // Skip Sentry for transient mobile network blips (the GET already
      // retried once in supabaseClient); wrap genuine errors in a real Error
      // so they group properly instead of "Object captured as exception".
      if (!isTransientNetworkError(error)) {
        Sentry.captureException(toError(error), {
          tags: { operation: 'getUserProfile', service: 'database' },
          contexts: { user: { id: userId } },
        });
      }
      return { data: null, error };
    }
  },

  /**
   * Get user's onboarding answers
   * @param {string} userId - The user's ID
   * @returns {Promise} - The onboarding answers
   */
  getOnboardingAnswers: async (userId) => {
    const maxRetries = 3;
    const baseDelay = 1000; // 1 second

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        logger.log(
          `[DatabaseService] Fetching onboarding answers for user ${userId} (attempt ${attempt}/${maxRetries})`
        );

        const { data, error } = await supabase
          .from('onboarding_answers')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (error) throw error;

        logger.log(`[DatabaseService] Successfully fetched onboarding answers for user ${userId}`);
        return { data, error: null };
      } catch (error) {
        const isLastAttempt = attempt === maxRetries;
        const isNetworkError =
          error.message?.includes('Load failed') ||
          error.message?.includes('NetworkError') ||
          error.message?.includes('Failed to fetch');

        logger.error(
          `[DatabaseService] Error fetching onboarding answers (attempt ${attempt}/${maxRetries}):`,
          {
            userId,
            errorMessage: error.message,
            errorCode: error.code,
            isNetworkError,
            willRetry: !isLastAttempt && isNetworkError,
          }
        );

        // Retry on network errors (common on mobile devices)
        if (isNetworkError && !isLastAttempt) {
          const delay = baseDelay * Math.pow(2, attempt - 1); // Exponential backoff
          logger.log(`[DatabaseService] Retrying in ${delay}ms...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        // On final attempt or non-network error, capture to Sentry
        Sentry.captureException(error, {
          tags: {
            operation: 'getOnboardingAnswers',
            service: 'database',
            attempt: attempt.toString(),
            isNetworkError: isNetworkError.toString(),
          },
          contexts: { user: { id: userId } },
        });
        return { data: null, error };
      }
    }

    // This should never be reached, but needed for completeness
    return { data: null, error: new Error('Unexpected: retry loop completed without return') };
  },

  /**
   * Check if user has completed onboarding
   * @param {string} userId - The user's ID
   * @returns {Promise<boolean>} - True if onboarding is complete
   */
  hasCompletedOnboarding: async (userId) => {
    const maxRetries = 3;
    const baseDelay = 1000; // 1 second

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        logger.log(
          `[DatabaseService] Checking onboarding status for user ${userId} (attempt ${attempt}/${maxRetries})`
        );

        // Use maybeSingle() instead of single() to avoid 406 errors
        // maybeSingle() returns null for data if no row found, instead of throwing PGRST116
        const { data, error } = await supabase
          .from('onboarding_answers')
          .select('user_id')
          .eq('user_id', userId)
          .maybeSingle();

        // If there's an error, log and potentially retry
        if (error) {
          // Log error details
          logger.error(
            `[DatabaseService] Error checking onboarding (attempt ${attempt}/${maxRetries}):`,
            {
              code: error.code,
              message: error.message,
              details: error.details,
              hint: error.hint,
            }
          );

          throw error;
        }

        // data is null if no row found, or the row data if found
        if (data) {
          logger.log(`[DatabaseService] User ${userId} has completed onboarding`);
          return true;
        } else {
          logger.log(`[DatabaseService] User ${userId} has not completed onboarding`);
          return false;
        }
      } catch (error) {
        const isLastAttempt = attempt === maxRetries;
        const isNetworkError =
          error.message?.includes('Load failed') ||
          error.message?.includes('NetworkError') ||
          error.message?.includes('Failed to fetch');

        // Log error with full context
        logger.error(
          `[DatabaseService] Failed to check onboarding status (attempt ${attempt}/${maxRetries}):`,
          {
            userId,
            errorMessage: error.message,
            errorCode: error.code,
            isNetworkError,
            willRetry: !isLastAttempt && isNetworkError,
          }
        );

        // Only retry on network errors
        if (isNetworkError && !isLastAttempt) {
          const delay = baseDelay * Math.pow(2, attempt - 1);
          logger.log(`[DatabaseService] Retrying in ${delay}ms...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        // On final attempt or non-network error, capture to Sentry and throw
        Sentry.captureException(error, {
          tags: {
            operation: 'hasCompletedOnboarding',
            service: 'database',
            attempt: attempt.toString(),
            isNetworkError: isNetworkError.toString(),
          },
          contexts: {
            user: { id: userId },
            error: {
              code: error.code,
              message: error.message,
              details: error.details,
            },
          },
        });

        // Throw error instead of defaulting to true - let caller decide how to handle
        throw error;
      }
    }

    // This should never be reached, but TypeScript needs it
    throw new Error('Unexpected: retry loop completed without return');
  },

  /**
   * Fetches persisted fit scores for a user with joined vehicle and model details.
   * This is the client-side equivalent of getUserFitScoresWithDetails from the Edge Function.
   * @param {string} userId - The user's ID
   * @returns {Promise<{data: Array<object> | null, error: Error | null}>} Array of recommendations or an error
   */
  getPersistedRecommendations: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('fit_scores')
        .select(`
          score_value,
          score_details,
          vehicles!inner (
            vehicle_id,
            vehicle_models!inner (
              make,
              model,
              year,
              official_images,
              year_range
            )
          )
        `)
        .eq('user_id', userId)
        .order('score_value', { ascending: false });

      if (error) throw error;

      if (!data) {
        return { data: [], error: null };
      }

      const formattedRecommendations = data
        .map((item) => {
          // The structure from Supabase might be slightly different without explicit casting
          // as in the Edge Function, so we access properties carefully.
          const vehicle = item.vehicles;
          const vehicleModel = vehicle?.vehicle_models; // Use optional chaining

          if (!vehicle || !vehicleModel) {
            logger.warn('Skipping item due to missing vehicle or vehicle_model data:', item);
            return null; // Or some default structure / filter it out later
          }

          return {
            vehicleDetails: {
              id: vehicle.vehicle_id,
              make: vehicleModel.make,
              model: vehicleModel.model,
              year: vehicleModel.year,
              year_range: vehicleModel.year_range,
            },
            fitScore: item.score_value,
            imageUrl:
              vehicleModel.official_images && vehicleModel.official_images.length > 0
                ? vehicleModel.official_images[0]
                : null,
            scoreDetails: item.score_details,
          };
        })
        .filter((rec) => rec !== null); // Filter out any nulls from malformed data

      return { data: formattedRecommendations, error: null };
    } catch (error) {
      logger.error('Error fetching persisted recommendations:', error.message);
      Sentry.captureException(error, {
        tags: { operation: 'getPersistedRecommendations', service: 'database' },
        contexts: { user: { id: userId } },
      });
      return { data: null, error };
    }
  },

  /**
   * Fetches top model recommendations for a user.
   * Joins user_model_fit_scores with vehicle_models.
   * @param {string} userId - The user's ID.
   * @param {number} limit - The maximum number of recommendations to fetch.
   * @returns {Promise<{data: Array<object> | null, error: Error | null}>} Array of recommendations or an error.
   */
  getUserModelRecommendations: async (userId, limit, scoreId = null) => {
    try {
      let query = supabase
        .from('user_model_fit_scores')
        .select(`
          user_model_fit_score_id,
          data_fidelity,
          image_fidelity,
          placeholder_image_url,
          generic_rationale,
          personalized_rationale,
          score_value,
          score_details,
          user_estimated_price_range:estimated_price_range,
          vehicle_models!inner (
            model_id,
            make,
            model,
            year_range,
            short_description,
            model_estimated_price_range:estimated_price_range,
            monthly_ownership_cost,
            depreciation_insight,
            trim,
            year,
            vehicle_model_images (
              image_url,
              angle_view,
              is_default_display
            )
          )
        `)
        .eq('user_id', userId);

      if (scoreId) {
        query = query.eq('user_model_fit_score_id', scoreId);
      } else {
        query = query.order('score_value', { ascending: false }).limit(limit);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (!data) {
        return { data: [], error: null };
      }

      // Use the new, centralized transformer function here
      const formattedRecommendations = data
        .map(DatabaseService.transformSupabasePayloadToRecommendation)
        .filter((rec) => rec !== null);

      return { data: formattedRecommendations, error: null };
    } catch (error) {
      logger.error('Error fetching user model recommendations:', error.message);
      Sentry.captureException(error, {
        tags: { operation: 'getUserModelRecommendations', service: 'database' },
        contexts: { user: { id: userId } },
      });
      return { data: null, error };
    }
  },

  /**
   * Transforms a raw Supabase fit score record into the clean recommendation object the UI expects.
   * Can be used for both initial fetch and real-time update payloads.
   * @param {object} score - A raw row from the user_model_fit_scores table with vehicle_models joined.
   * @returns {object} A clean, flattened recommendation object.
   */
  transformSupabasePayloadToRecommendation: (score) => {
    // Handle both nested (initial fetch) and flat (real-time) structures
    const vehicleModel = score.vehicle_models || score;

    if (!vehicleModel || !vehicleModel.model_id) {
      // Log a warning and return a minimal object or null to be filtered
      logger.warn(
        `[DatabaseService] transformSupabasePayloadToRecommendation: vehicle_models is null for score_id: ${score.user_model_fit_score_id}.`
      );
      return null;
    }

    const allModelImages = vehicleModel.vehicle_model_images || [];
    let primaryImageUrl = null;

    const defaultImage = allModelImages.find((img) => img.is_default_display);
    if (defaultImage) {
      primaryImageUrl = defaultImage.image_url;
    } else if (allModelImages.length > 0) {
      primaryImageUrl = allModelImages[0].image_url;
    }

    // Determine price range: prefer user-specific, fallback to model-level
    const finalPriceRange =
      score.user_estimated_price_range || vehicleModel.model_estimated_price_range;

    // NOTE: This now returns ALL fields needed by the reactive card and the hook.
    return {
      user_model_fit_score_id: score.user_model_fit_score_id,
      modelId: vehicleModel.model_id,
      make: vehicleModel.make,
      model: vehicleModel.model,
      yearRange: vehicleModel.year_range,
      trim: vehicleModel.trim,
      year: vehicleModel.year,
      scoreValue: score.score_value,

      data_fidelity: score.data_fidelity,
      image_fidelity: score.image_fidelity,

      primaryImageUrl: primaryImageUrl, // The final, confirmed image
      placeholder_image_url: score.placeholder_image_url, // The initial placeholder image
      allModelImages: allModelImages,

      shortDescription: vehicleModel.short_description,
      generic_rationale: score.generic_rationale,
      personalized_rationale: score.personalized_rationale,
      scoreDetailsExplanations: score.score_details?.explanations || { good_fit: [], bad_fit: [] },

      estimatedPriceRange: finalPriceRange,
      monthlyOwnershipCost: vehicleModel.monthly_ownership_cost,
      depreciationInsight: vehicleModel.depreciation_insight,
    };
  },

  /**
   * Adds a user interaction record for a vehicle model.
   * @param {string} userId - The user's ID.
   * @param {string} modelId - The model's ID.
   * @param {string} actionType - The type of interaction (e.g., 'like', 'dislike', 'view_details').
   * @param {object} [details] - Additional details about the interaction.
   * @returns {Promise<{data: Array<object> | null, error: Error | null}>} Result of the insert operation.
   */
  addUserModelInteraction: async (userId, modelId, actionType, details = {}) => {
    try {
      const { data, error } = await supabase
        .from('user_model_interactions')
        .insert([
          {
            user_id: userId,
            model_id: modelId,
            action_type: actionType,
            details: details,
            interacted_at: new Date().toISOString(), // Assuming 'interacted_at' or similar timestamp column
          },
        ])
        .select(); // .select() returns the inserted rows

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      logger.error('Error adding user model interaction:', error.message);
      Sentry.captureException(error, {
        tags: { operation: 'addUserModelInteraction', service: 'database' },
        contexts: { user: { id: userId }, model: { id: modelId } },
      });
      return { data: null, error };
    }
  },

  /**
   * Fetches the fit score for a specific user and vehicle model.
   * @param {string} userId - The user's ID.
   * @param {string} modelId - The model's ID.
   * @returns {Promise<{data: object | null, error: Error | null}>} The fit score object or an error.
   */
  getUserModelFitScore: async (userId, modelId) => {
    try {
      if (!userId || !modelId) {
        throw new Error('User ID and Model ID are required to fetch fit score.');
      }

      const { data, error } = await supabase
        .from('user_model_fit_scores')
        .select('user_model_fit_score_id, score_value, score_details, data_fidelity')
        .eq('user_id', userId)
        .eq('model_id', modelId)
        .single();

      if (error) {
        // 'single' throws an error if no row is found, which we can treat as a "not found" case.
        if (error.code === 'PGRST116') {
          logger.log(`No fit score found for user ${userId} and model ${modelId}.`);
          return { data: null, error: null }; // Not a system error, just no data.
        }
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      logger.error('Error fetching user model fit score:', error.message);
      Sentry.captureException(error, {
        tags: { operation: 'getUserModelFitScore', service: 'database' },
        contexts: { user: { id: userId }, model: { id: modelId } },
      });
      return { data: null, error };
    }
  },
};

export default DatabaseService;
